import { normalizeString } from "./formats"
import type { LoanDocument } from "./document-types"
import { EquifaxPrequalReport } from "./equifax-credit-report"
import type { IdentityVerificationVersion } from "./loan-application"

export enum AdditionalInfoRequirementType {
  GovernmentPhotoId = 'government_photo_id',
  MortgageDocumentation = 'mortgage_documentation',
  NameMismatchReview = 'name_mismatch_review',
}

export const additionalInfoRequirementTypeValues = Object.values(
  AdditionalInfoRequirementType
) as AdditionalInfoRequirementType[]

export enum RequirementReasonCode {
  NameMismatch = 'name_mismatch',
  AddressVerification = 'address_verification',
  Mortgage = 'mortgage',
}

export const requirementReasonCodeValues = Object.values(
  RequirementReasonCode
) as RequirementReasonCode[]

export enum LifeEventType {
  RecentlyMarried = 'recently_married',
  RecentlyDivorced = 'recently_divorced',
  NameChange = 'name_change',
  Other = 'other',
}

export const lifeEventTypeValues = Object.values(
  LifeEventType
) as LifeEventType[]

export type NameMismatchDetails = {
  hadLifeEvent: boolean | null
  lifeEventType: LifeEventType | null
  supportingDocument: LoanDocument[] | null
}

export type AdditionalInfoReasonDetails = {
  nameMismatchDetails?: NameMismatchDetails | null
}

export type AdditionalInfoRequirement = {
  type: AdditionalInfoRequirementType
  reasonCodes: RequirementReasonCode[]
  documents?: LoanDocument[]
  reasonDetails?: AdditionalInfoReasonDetails
}

export type RequiredAdditionalInformation = {
  primary?: AdditionalInfoRequirement[]
  coBorrower?: AdditionalInfoRequirement[]
}

// Preserve previously uploaded documents when requirements are recomputed.
export function mergeRequiredAdditionalInformationDocuments(
  prev: RequiredAdditionalInformation | undefined | null,
  next: RequiredAdditionalInformation
): RequiredAdditionalInformation {
  const mergeLists = (
    prevList: AdditionalInfoRequirement[] | undefined,
    nextList: AdditionalInfoRequirement[] | undefined
  ): AdditionalInfoRequirement[] | undefined => {
    if (!nextList?.length) return undefined
    return nextList.map((req, i) => {
      const byIndex = prevList?.[i]?.type === req.type ? prevList[i] : undefined
      const byType = prevList?.find(p => p.type === req.type)
      const old = byIndex ?? byType
      let merged = { ...req }
      if (old?.documents?.length) {
        merged = { ...merged, documents: old.documents }
      }
      if (old?.reasonDetails) {
        merged = {
          ...merged,
          reasonDetails: {
            ...req.reasonDetails,
            ...old.reasonDetails,
          },
        }
      }
      return merged
    })
  }
  const out: RequiredAdditionalInformation = {}
  const primary = mergeLists(prev?.primary, next.primary)
  const coBorrower = mergeLists(prev?.coBorrower, next.coBorrower)
  if (primary?.length) out.primary = primary
  if (coBorrower?.length) out.coBorrower = coBorrower
  return out
}

const ADDRESS_VERIFICATION_KEYS = ['addressVerification', 'addressAffiliation']
const ADDRESS_FAIL_VALUES = ['n', 'no']

export const requiresAdditionalInfoFromDitDetails = (details: { key: string; value: string }[]): boolean => {
  return (details || []).some(d => ADDRESS_VERIFICATION_KEYS.includes(d.key) &&
    ADDRESS_FAIL_VALUES.includes((d.value || '').trim().toLowerCase()))
}

export const hasMatchingFirstAndLastName = (
  appFirstName: string, appLastName: string,
  reportFirstName: string, reportLastName: string
): boolean => {
  const normalizedAppFirst = normalizeString(appFirstName)
  const normalizedAppLast = normalizeString(appLastName)
  const normalizedReportFirst = normalizeString(reportFirstName)
  const normalizedReportLast = normalizeString(reportLastName)

  return normalizedAppFirst === normalizedReportFirst && normalizedAppLast === normalizedReportLast
}

export const requiresGovernmentIdForNameMismatch = (
  appFirstName: string, appLastName: string,
  report: { subjectName: { firstName?: string; middleName?: string; lastName?: string } }
): boolean => {
  const r = report?.subjectName
  if (!r) return false
  const reportFirstName = r.firstName ?? ''
  const reportLastName = r.lastName ?? ''
  return !hasMatchingFirstAndLastName(appFirstName, appLastName, reportFirstName, reportLastName)
}

export const buildRequirementsForBorrower = (
  prequalReport: EquifaxPrequalReport | null,
  ditDetails: { key: string; value: string }[],
  appFirstName: string, appLastName: string,
  hasMortgage: string | undefined,
  identityVerificationVersion?: IdentityVerificationVersion
): AdditionalInfoRequirement[] => {
  const requirements: AdditionalInfoRequirement[] = []

  const hasNameMismatch = prequalReport !== null && requiresGovernmentIdForNameMismatch(
    appFirstName, appLastName, prequalReport
  )

  const hasAddressIssue = requiresAdditionalInfoFromDitDetails(ditDetails)

  if (identityVerificationVersion === 'persona' && hasNameMismatch) {
    requirements.push({
      type: AdditionalInfoRequirementType.NameMismatchReview,
      reasonCodes: [RequirementReasonCode.NameMismatch],
      reasonDetails: {
        nameMismatchDetails: {
          hadLifeEvent: null,
          lifeEventType: null,
          supportingDocument: null,
        },
      },
    })
  } else if (identityVerificationVersion !== 'persona' && (hasNameMismatch || hasAddressIssue)) {
    const reasonCodes: RequirementReasonCode[] = []

    if (hasNameMismatch) { reasonCodes.push(RequirementReasonCode.NameMismatch) }
    if (hasAddressIssue) { reasonCodes.push(RequirementReasonCode.AddressVerification) }

    const govIdReq: AdditionalInfoRequirement = {
      type: AdditionalInfoRequirementType.GovernmentPhotoId,
      reasonCodes,
      documents: [],
    }
    if (hasNameMismatch) {
      govIdReq.reasonDetails = {
        nameMismatchDetails: {
          hadLifeEvent: null,
          lifeEventType: null,
          supportingDocument: null,
        },
      }
    }
    requirements.push(govIdReq)
  }

  if (prequalReport && hasMortgage === 'yes' && !prequalReport.hasActiveMortgageTradeline()) {
    requirements.push({
      type: AdditionalInfoRequirementType.MortgageDocumentation,
      reasonCodes: [RequirementReasonCode.Mortgage],
      documents: [],
    })
  }

  return requirements
}

export const hasRequiredAdditionalInformation = (r?: RequiredAdditionalInformation | null): boolean => {
  if (!r) return false
  const primary = r.primary?.length ?? 0
  const coBorrower = r.coBorrower?.length ?? 0
  return primary + coBorrower > 0
}


// Every listed requirement has the documents or questionnaire response it requires.
export const additionalInfoRequirementsSatisfied = (r?: RequiredAdditionalInformation | null): boolean => {
  if (!hasRequiredAdditionalInformation(r)) return true
  const requirementSatisfied = (requirement: AdditionalInfoRequirement): boolean => {
    if (requirement.type !== AdditionalInfoRequirementType.NameMismatchReview) {
      return (requirement.documents?.length ?? 0) > 0
    }

    const details = requirement.reasonDetails?.nameMismatchDetails
    if (details?.hadLifeEvent === false) {
      return true
    }

    return details?.hadLifeEvent === true &&
      details.lifeEventType !== null &&
      (details.supportingDocument?.length ?? 0) > 0
  }
  const listOk = (list?: AdditionalInfoRequirement[]) =>
    (list ?? []).every(requirementSatisfied)
  return listOk(r?.primary) && listOk(r?.coBorrower)
}

export const loanDocumentsFromRequiredAdditionalInformation = (
  r?: RequiredAdditionalInformation | null
): LoanDocument[] => {
  if (!r) return []
  const additionalInfoDocuments: LoanDocument[] = []
  const addFromRequirement = (req: AdditionalInfoRequirement) => {
    if (req.documents?.length) {
      additionalInfoDocuments.push(...req.documents)
    }
    const supporting = req.reasonDetails?.nameMismatchDetails?.supportingDocument
    if (supporting?.length) {
      additionalInfoDocuments.push(...supporting)
    }
  }
  r.primary?.forEach(addFromRequirement)
  r.coBorrower?.forEach(addFromRequirement)
  return additionalInfoDocuments
}
